import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, IconButton, TableCell, TableBody, TableRow, TableHead, Table, CircularProgress, MenuItem, Menu, TableContainer, TextField, Tooltip } from '@mui/material';
import { ExpandMoreOutlined, MoreVert, Edit, Delete, AccountBalanceWalletOutlined, SupervisorAccountOutlined, ReceiptLongOutlined, ConfirmationNumberOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { api } from '../../../services/api';
import CreateCajaModal from "../../modales/caja/CreateCajaModal";
import UpdateCajaModal from "../../modales/caja/UpdateCajaModal";
import DeleteCajaModal from "../../modales/caja/DeleteCajaModal";
import RendirEfectivoModal from "../../modales/caja/RendirEfectivoModal";
import UsuariosSaldosModal from "../../modales/caja/UsuariosSaldosModal";
import CierreZetaModal from "../../modales/caja/CierreZetaModal";
import PreimpresosPanel from "./PreimpresosPanel";
import PreimpresosMasivoPanel from "./PreimpresosMasivoPanel";
import { toast } from 'react-toastify';

const CajaTab = ({ eventoId, vendedores, allTarifas, allCajas,
  loadingCajas,
  refetchCajas, isMobile, onSuccess, onError }) => {

  const [openCreateCajaModal, setOpenCreateCajaModal] = useState(false);
  const [loadingCreateCaja, setLoadingCreateCaja] = useState(false);
  const [openEditCajaModal, setOpenEditCajaModal] = useState(false);
  const [editingCajaId, setEditingCajaId] = useState(null);
  const [loadingEditCaja, setLoadingEditCaja] = useState(false);
  const [openDeleteCajaModal, setOpenDeleteCajaModal] = useState(false);
  const [deletingCajaId, setDeletingCajaId] = useState(null);
  const [deletingCajaNombre, setDeletingCajaNombre] = useState("");
  const [loadingDeleteCaja, setLoadingDeleteCaja] = useState(false);

  const [cajasConSaldos, setCajasConSaldos] = useState({});
  const [loadingSaldos, setLoadingSaldos] = useState(false);
  const [openRendirModal, setOpenRendirModal] = useState(false);
  const [cajaParaRendir, setCajaParaRendir] = useState(null);
  const [loadingRendir, setLoadingRendir] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCajaId, setMenuCajaId] = useState(null);

  const [openCierreZeta, setOpenCierreZeta] = useState(false);
  const [cierreZetaCajaId, setCierreZetaCajaId] = useState(null);

  const [preimpresosCajaId, setPreimpresosCajaId] = useState(null);
  const [openPreimpresosMasivo, setOpenPreimpresosMasivo] = useState(false);

  const [anchorMenu, setAnchorMenu] = useState(null);
  const [openTesorerosModal, setOpenTesorerosModal] = useState(false);

  // búsqueda de caja: por nombre de caja, vendedor (email), nombre del vendedor o id de click macro
  const [searchTerm, setSearchTerm] = useState("");


  useEffect(() => {
    if (eventoId) {
      fetchSaldosEfectivo();
    }
  }, [eventoId]);

  const fetchSaldosEfectivo = async () => {
    setLoadingSaldos(true);
    const res = await api.rendiciones.getCajasRendiciones(eventoId);
    if (res.status === "success") {
      const saldosPorUsuario = {};
      res?.data?.forEach(caja => {
        saldosPorUsuario[caja.usuario] = caja;
      });
      setCajasConSaldos(saldosPorUsuario);
    }
    setLoadingSaldos(false);
  };

  const handleCrearCaja = async (data) => {
    setLoadingCreateCaja(true);
    const res = await api.caja.createCaja(eventoId, {
      caja: data.caja,
      idusuario: data.idusuario,
      preimpreso: data.preimpreso || false,
      tarifas: data.tarifas || [],
      ...(data.cajamacro && { cajamacro: data.cajamacro })
    });
    setLoadingCreateCaja(false);

    if (res.status === "success") {
      setOpenCreateCajaModal(false);
      toast.success("Caja creada correctamente", {
        position: "top-right",
        autoClose: 3000,
      });
      refetchCajas();
      fetchSaldosEfectivo();
      // si la caja es preimpresa, abrir el panel de generación de preimpresos
      if (data.preimpreso && res.data?.id) {
        setPreimpresosCajaId(res.data.id);
      }
    } else {
      toast.error(res.message || "Error al crear caja", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // estado de stock de una tarifa preimpresa: agotado (0), bajo (<=20% restante) u ok
  const getStockPreimpresoStatus = (tarifa) => {
    if (!tarifa?.preimpreso) return null;
    const total = tarifa.total_preimpresos ?? 0;
    const restantes = tarifa.restantes_preimpresos ?? 0;
    if (restantes === 0) return "agotado";
    if (total > 0 && restantes / total <= 0.2) return "bajo";
    return "ok";
  };

  const allCajasOrdenadas = Array.isArray(allCajas)
    ? [...allCajas].sort((a, b) => {
      const regex = /(\d+)/;
      const aNum = a.caja.match(regex) ? parseInt(a.caja.match(regex)[1], 10) : 0;
      const bNum = b.caja.match(regex) ? parseInt(b.caja.match(regex)[1], 10) : 0;
      if (a.caja.replace(regex, '') === b.caja.replace(regex, '')) {
        return aNum - bNum;
      }
      return a.caja.localeCompare(b.caja, 'es', { numeric: true });
    })
    : [];

  const searchTermLower = searchTerm.trim().toLowerCase();
  const allCajasFiltradas = searchTermLower
    ? allCajasOrdenadas.filter(caja =>
      caja.caja?.toLowerCase().includes(searchTermLower) ||
      caja.usuario?.toLowerCase().includes(searchTermLower) ||
      caja.nombre_completo?.toLowerCase().includes(searchTermLower) ||
      caja.cajamacro?.toLowerCase().includes(searchTermLower)
    )
    : allCajasOrdenadas;

  const cajasPorUsuario = allCajasFiltradas.length > 0
    ? allCajasFiltradas.reduce((acc, caja) => {
      if (!acc[caja.usuario]) {
        acc[caja.usuario] = {
          cajas: [],
          nombre_completo: caja.nombre_completo,
          usuario: caja.usuario
        };
      }
      acc[caja.usuario].cajas.push(caja);
      return acc;
    }, {})
    : {};

  const handleEliminarCaja = async () => {
    setLoadingDeleteCaja(true);
    const res = await api.caja.deleteCaja(eventoId, deletingCajaId);
    setLoadingDeleteCaja(false);

    if (res.status === "success") {
      setOpenDeleteCajaModal(false);
      setDeletingCajaId(null);
      setDeletingCajaNombre("");
      setAnchorEl(null);
      setMenuCajaId(null);
      toast.success("Caja eliminada correctamente", {
        position: "top-right",
        autoClose: 3000,
      });
      refetchCajas();
      fetchSaldosEfectivo();
    } else {
      toast.error(res.message || "Error al eliminar caja", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleEditarCaja = async (data) => {
    setLoadingEditCaja(true);
    const res = await api.caja.updateCaja(eventoId, editingCajaId, {
      caja: data.caja,
      idusuario: data.idusuario,
      preimpreso: data.preimpreso || false,
      tarifas: data.tarifas || [],
      ...(data.cajamacro && { cajamacro: data.cajamacro })
    });
    setLoadingEditCaja(false);

    if (res.status === "success") {
      setOpenEditCajaModal(false);
      setEditingCajaId(null);
      setAnchorEl(null);
      setMenuCajaId(null);
      toast.success("Caja editada correctamente", {
        position: "top-right",
        autoClose: 3000,
      });
      refetchCajas();
      fetchSaldosEfectivo();
    } else {
      toast.error(res.message || "Error al editar caja", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleRendirEfectivo = async (data) => {
    if (!cajaParaRendir) return;

    setLoadingRendir(true);
    const res = await api.rendiciones.rendirEfectivo(eventoId, {
      vendedor_id: cajaParaRendir.vendedor_id || cajaParaRendir.idusuario,
      monto_efectivo: data.monto_efectivo,
      observaciones: data.observaciones
    });
    setLoadingRendir(false);

    if (res.status === "success") {
      setOpenRendirModal(false);
      setCajaParaRendir(null);
      toast.success("Rendición procesada correctamente");
      fetchSaldosEfectivo();
    } else {
      toast.error(res.message || "Error al procesar rendición");
    }
  };

  const abrirModalRendir = (usuario, caja) => {
    const saldoCaja = cajasConSaldos[usuario];
    if (saldoCaja && saldoCaja.saldo_caja > 0) {
      setCajaParaRendir({
        ...saldoCaja,
        caja: caja.caja,
        nombre_completo: caja.nombre_completo,
        vendedor_id: saldoCaja.vendedor_id || caja.idusuario
      });
      setOpenRendirModal(true);
    }
  };

  const handleMenuClick = (event, cajaId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuCajaId(cajaId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCajaId(null);
  };

  const handleMenu2 = (event) => {
    event.stopPropagation();
    setAnchorMenu(event.currentTarget);
  };

  const handleClose2 = () => {
    setAnchorMenu(null);
  };

  const abrirTesorerosModal = () => {
    setOpenTesorerosModal(true);
    handleClose2();
  };

  const openEditModal = (cajaId) => {
    setEditingCajaId(cajaId);
    setOpenEditCajaModal(true);
  };

  const openDeleteModal = (cajaId, cajaNombre) => {
    setDeletingCajaId(cajaId);
    setDeletingCajaNombre(cajaNombre);
    setOpenDeleteCajaModal(true);
    handleMenuClose();
  };

  const openCierreZetaModal = (cajaId) => {
    setCierreZetaCajaId(cajaId);
    setOpenCierreZeta(true);
    handleMenuClose();
  };

  const openPreimpresosPanel = (cajaId) => {
    setPreimpresosCajaId(cajaId);
    handleMenuClose();
  };

  if (loadingCajas) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (preimpresosCajaId) {
    return (
      <PreimpresosPanel
        eventoId={eventoId}
        cajaId={preimpresosCajaId}
        onBack={() => {
          setPreimpresosCajaId(null);
          refetchCajas();
        }}
      />
    );
  }

  if (openPreimpresosMasivo) {
    return (
      <PreimpresosMasivoPanel
        eventoId={eventoId}
        cajas={allCajas}
        onBack={() => {
          setOpenPreimpresosMasivo(false);
          refetchCajas();
        }}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap" }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#5656ff",
          }}
        >
          Cajas del Evento ({allCajas?.length || 0})
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "end", gap: 1 }}>

          <Button
            variant="outlined"
            sx={{
              marginLeft: "0 !important",
              backgroundColor: "#5656FF",
              color: "white",
              padding: "5px 10px",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: window.innerWidth <= 576 ? "13px" : "14px",
            }}
            onClick={() => setOpenCreateCajaModal(true)}
          >
            Crear Caja
          </Button>
          <IconButton
            onClick={handleMenu2}
            size="small"
            sx={{ color: "#666" }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <TextField
        size="small"
        placeholder="Buscar por caja, vendedor o click macro"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          fontFamily: "Bricolage Grotesque",
          minWidth: "320px",
          mb: 2,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#B2BAC2',
            },
            '&:hover fieldset': {
              borderColor: '#5656ff',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#5656ff',
            },
          }
        }}
      />
      <Box sx={{ mb: 4 }}>
        {Object.keys(cajasPorUsuario)?.length === 0 ? (
          <Box sx={{
            textAlign: 'center',
            py: 4,
            border: '1px dashed #ccc',
            borderRadius: 2,
            backgroundColor: '#f9f9f9'
          }}>
            <Typography color="text.secondary">
              {searchTerm ? "No se encontraron cajas que coincidan con la búsqueda" : "No hay cajas creadas para este evento"}
            </Typography>
          </Box>
        ) : (
          Object.entries(cajasPorUsuario).map(([usuario, data]) => {
            const saldoCaja = cajasConSaldos[usuario];
            const tieneEfectivo = saldoCaja && saldoCaja.saldo_caja > 0;

            // alerta de stock de preimpresos, visible sin necesidad de desplegar la caja
            const estadosStock = data.cajas
              .filter(caja => caja.preimpreso)
              .flatMap(caja => caja.tarifas || [])
              .map(getStockPreimpresoStatus)
              .filter(Boolean);
            const alertaStock = estadosStock.includes("agotado")
              ? "agotado"
              : estadosStock.includes("bajo")
                ? "bajo"
                : null;

            return (
              <Accordion key={usuario} sx={{ boxShadow: 1, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                  <Box sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "flex-start" : "center"
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={`${data.cajas[0].caja}`}
                          size="small"
                          sx={{
                            backgroundColor: "#e8e8ff",
                            color: "#5656ff",
                            fontWeight: 600
                          }}
                        />
                        <Typography sx={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#0b0062"
                        }}>
                          {data.nombre_completo}
                        </Typography>
                      </Box>
                      <Typography sx={{
                        fontSize: "14px",
                        color: "#666",
                        mt: 0.5
                      }}>
                        {data?.usuario}
                      </Typography>
                      {data.cajas[0].cajamacro && (
                        <Typography sx={{
                          fontSize: "14px",
                          color: "#666",
                          mt: 0.5
                        }}>
                          Click macro: {data.cajas[0].cajamacro}
                        </Typography>
                      )}

                      {/* SALDO */}
                      {loadingSaldos ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            Cargando saldo...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{
                          fontSize: "16px",
                          color: tieneEfectivo ? "#2e7d32" : "#666",
                          fontWeight: tieneEfectivo ? 600 : 400,
                          mt: 0.5,
                          display: "flex",
                          alignItems: "center"
                        }}>
                          <AccountBalanceWalletOutlined sx={{ fontSize: 16, mr: 0.5 }} />
                          ${(saldoCaja?.saldo_caja || 0).toLocaleString('es-AR')} en efectivo
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {alertaStock && (
                        <Tooltip title={alertaStock === "agotado" ? "Hay una tarifa preimpresa sin stock" : "Queda poco stock de preimpresos"}>
                          <WarningAmberOutlined
                            fontSize="small"
                            sx={{ color: alertaStock === "agotado" ? "#c62828" : "#e65100" }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Tooltip>
                      )}
                      {/* <Chip
                        label={`${data.cajas[0].caja}`}
                        size="small"
                        sx={{
                          backgroundColor: "#e8e8ff",
                          color: "#5656ff",
                          fontWeight: 500
                        }}
                      /> */}

                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!tieneEfectivo || loadingSaldos}
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirModalRendir(usuario, data.cajas[0]);
                        }}
                        sx={{
                          color: tieneEfectivo ? "#5656ff" : "#ccc",
                          borderColor: tieneEfectivo ? "#5656ff" : "#ccc",
                          fontSize: "11px",
                          minWidth: "50px",
                          height: "28px",
                          '&:hover': {
                            borderColor: tieneEfectivo ? "#5656ff" : "#ccc",
                            backgroundColor: tieneEfectivo ? "#e8e8ff" : "transparent"
                          }
                        }}
                      >
                        Rendir
                      </Button>

                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, data.cajas[0].id)}
                        sx={{ color: "#666" }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  {data.cajas.map((caja) => (
                    <Box key={caja.id}>
                      {/* DETALLE CAJA */}
                      {saldoCaja && (
                        <Box sx={{ mb: 2, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            Ventas realizadas: <strong>{saldoCaja.total_ventas || 0}</strong>
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            Monto total <em>(QR + Efectivo)</em>: <strong>${(saldoCaja.monto_total || 0).toLocaleString('es-AR')}</strong>
                          </Typography>
                        </Box>
                      )}

                      {/* TARIFAS DE LA CAJA */}
                      <Typography sx={{ color: "#0b0062", fontWeight: "600", mb: 1 }}>Tarifas en esta caja</Typography>
                      <TableContainer
                        sx={{
                          maxWidth: '100%',
                          overflowX: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          '&::-webkit-scrollbar': {
                            height: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '3px',
                            '&:hover': {
                              backgroundColor: '#a1a1a1',
                            },
                          },
                        }}
                      >
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: "#E6E6FF" }}>
                            <TableRow sx={{ borderColor: "#E6E6FF", textAlign: "center" }}>
                              <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Concepto</TableCell>
                              <TableCell align="center" sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Tipo</TableCell>
                              <TableCell align="center" sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Precio</TableCell>
                              {caja.preimpreso && (
                                <TableCell align="center" sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Disponible</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {caja?.tarifas?.map((tarifa) => (
                              <TableRow key={tarifa.id}>
                                <TableCell sx={{ display: "flex", alignItems: "center", fontFamily: "Bricolage Grotesque" }}>
                                  <span style={{
                                    display: "inline-block",
                                    width: 18,
                                    height: 10,
                                    borderRadius: 4,
                                    background: (() => {
                                      try {
                                        const c = JSON.parse(tarifa.color);
                                        return `rgba(${c.r},${c.g},${c.b},${c.a})`;
                                      } catch { return "#eee"; }
                                    })(),
                                    marginRight: 8
                                  }} />
                                  {tarifa.concepto}
                                </TableCell>
                                <TableCell align="center" className="fw-medium" sx={{ fontFamily: "Bricolage Grotesque", }}>
                                  {tarifa.idtipo === 1 ? "Ticket" : tarifa.idtipo === 2 ? "Voucher" : "Tarifa"}
                                </TableCell>
                                <TableCell align="center" className="fw-medium" sx={{ fontFamily: "Bricolage Grotesque", }}>
                                  ${tarifa.precio.toLocaleString('es-AR')}
                                </TableCell>
                                {caja.preimpreso && (
                                  <TableCell align="center" sx={{ fontFamily: "Bricolage Grotesque" }}>
                                    {tarifa.preimpreso ? (
                                      <Chip
                                        label={`${tarifa.restantes_preimpresos ?? 0} disp.`}
                                        size="small"
                                        sx={{
                                          backgroundColor: {
                                            agotado: "#fdecea",
                                            bajo: "#fff4e5",
                                            ok: "#e8f5e8"
                                          }[getStockPreimpresoStatus(tarifa)],
                                          color: {
                                            agotado: "#c62828",
                                            bajo: "#e65100",
                                            ok: "#2e7d32"
                                          }[getStockPreimpresoStatus(tarifa)],
                                          fontWeight: 600,
                                          fontSize: "12px"
                                        }}
                                      />
                                    ) : (
                                      <Typography sx={{ color: "#999", fontSize: "12px" }}>—</Typography>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 150,
            '& .MuiMenuItem-root': {
              fontSize: '14px',
              fontFamily: 'Bricolage Grotesque',
            }
          }
        }}
      >
        <MenuItem onClick={() => openEditModal(menuCajaId)}>
          <Edit fontSize="small" sx={{ mr: 1, color: "#5656FF" }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => openCierreZetaModal(menuCajaId)}>
          <ReceiptLongOutlined fontSize="small" sx={{ mr: 1, color: "#5656FF" }} />
          Reporte Z
        </MenuItem>
        {(() => {
          const caja = Object.values(cajasPorUsuario)
            .flatMap(user => user.cajas)
            .find(c => c.id === menuCajaId);
          return caja?.preimpreso ? (
            <MenuItem onClick={() => openPreimpresosPanel(menuCajaId)}>
              <ConfirmationNumberOutlined fontSize="small" sx={{ mr: 1, color: "#5656FF" }} />
              Preimpresos
            </MenuItem>
          ) : null;
        })()}
        <MenuItem
          onClick={() => {
            const caja = Object.values(cajasPorUsuario)
              .flatMap(user => user.cajas)
              .find(c => c.id === menuCajaId);
            openDeleteModal(menuCajaId, caja?.caja || "");
          }}
          sx={{ color: "#d32f2f" }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={handleClose2}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180,
            '& .MuiMenuItem-root': {
              fontSize: '14px',
              fontFamily: 'Bricolage Grotesque',
            }
          }
        }}
      >
        <MenuItem onClick={abrirTesorerosModal}>
          <SupervisorAccountOutlined fontSize="small" sx={{ mr: 1, color: "#5656ff" }} />
          Saldos
        </MenuItem>
        <MenuItem onClick={() => { setOpenPreimpresosMasivo(true); handleClose2(); }}>
          <ConfirmationNumberOutlined fontSize="small" sx={{ mr: 1, color: "#5656ff" }} />
          Carga masiva de preimpresos
        </MenuItem>
      </Menu>

      <CreateCajaModal
        open={openCreateCajaModal}
        onClose={() => setOpenCreateCajaModal(false)}
        onCrear={handleCrearCaja}
        eventoId={eventoId}
        vendedores={vendedores}
        allTarifas={allTarifas}
        loading={loadingCreateCaja}
      />
      <UpdateCajaModal
        open={openEditCajaModal}
        onClose={() => {
          setOpenEditCajaModal(false);
          setEditingCajaId(null);
        }}
        onEditar={handleEditarCaja}
        eventoId={eventoId}
        cajaId={editingCajaId}
        vendedores={vendedores}
        allTarifas={allTarifas}
        loading={loadingEditCaja}
      />

      <DeleteCajaModal
        open={openDeleteCajaModal}
        onClose={() => {
          setOpenDeleteCajaModal(false);
          setDeletingCajaId(null);
          setDeletingCajaNombre("");
        }}
        onConfirm={handleEliminarCaja}
        cajaNombre={deletingCajaNombre}
        loading={loadingDeleteCaja}
      />

      <RendirEfectivoModal
        open={openRendirModal}
        onClose={() => {
          setOpenRendirModal(false);
          setCajaParaRendir(null);
        }}
        onRendir={handleRendirEfectivo}
        caja={cajaParaRendir}
        loading={loadingRendir}
      />

      <CierreZetaModal
        open={openCierreZeta}
        onClose={() => {
          setOpenCierreZeta(false);
          setCierreZetaCajaId(null);
        }}
        eventoId={eventoId}
        cajaId={cierreZetaCajaId}
      />

      <UsuariosSaldosModal
        open={openTesorerosModal}
        onClose={() => setOpenTesorerosModal(false)}
        eventoId={eventoId}
      />

    </Box>
  );
};

export default CajaTab;